import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid,
  Typography,
  useTheme,
  Box,
  CircularProgress,
  Pagination,
  PaginationItem,
  Select,
  MenuItem,
  FormControl,
  alpha,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import StarsIcon from "@mui/icons-material/Stars";
import ArrowCircleRightOutlinedIcon from "@mui/icons-material/ArrowCircleRightOutlined";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Box as CustomBox } from "components";
import { useNavigate } from "react-router-dom";
import {
  fetchTopics,
  changePage,
  changePageSize,
} from "../../../../redux/actions/topicsActions";
import moment from "moment";

function GridView() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const isLight = theme.palette.mode === "light";
  const dispatch = useDispatch();

  // Get data from Redux store
  const { data, loading, totalItems, filters } = useSelector(
    (state) => state.topics,
  );

  // Handle page change
  const handlePageChange = (event, page) => {
    dispatch(changePage(page));
  };

  // Handle page size change
  const handlePageSizeChange = (event) => {
    dispatch(changePageSize(event.target.value));
  };

  // Load topics if not already loaded
  useEffect(() => {
    if (data.length === 0 && !loading) {
      dispatch(fetchTopics());
    }
  }, [dispatch, data.length, loading]);

  // Show loading spinner if loading
  if (loading && data.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={300}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show empty state if no data
  if (!loading && data.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={300}
      >
        <Typography variant="h6" color="text.secondary">
          {t("topics.noTopicsFound")}
        </Typography>
      </Box>
    );
  }

  const showingFrom = Math.min(
    (filters.page - 1) * filters.limit + 1,
    totalItems,
  );
  const showingTo = Math.min(filters.page * filters.limit, totalItems);

  return (
    <Box>
      <Grid container spacing={3}>
        {data.map((topic) => {
          // Determine contribution status
          const isContributed = topic.is_current_user_contribute || false;
          const statusColor = isContributed
            ? theme.palette.success.main
            : theme.palette.warning.main;

          return (
            <Grid
              key={topic.id || topic.topic_id}
              item
              size={{ xs: 12, md: 6, lg: 3 }}
            >
              <CustomBox
                component="section"
                onClick={() =>
                  navigate(`/single_topic/${topic.id || topic.topic_id}`)
                }
                sx={{
                  position: "relative",
                  height: "100%",
                  minHeight: 260,
                  borderRadius: 3,
                  cursor: "pointer",
                  background: isLight
                    ? "rgba(255, 255, 255, 0.6)"
                    : "rgba(255, 255, 255, 0.03)",
                  backdropFilter: "blur(20px)",
                  border: "none",
                  boxShadow: isLight
                    ? "0 4px 16px rgba(0, 0, 0, 0.04)"
                    : "0 4px 16px rgba(0, 0, 0, 0.3)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  display: "flex",
                  flexDirection: "column",
                  p: 2.5,
                  "&:hover": {
                    transform: "translateY(-6px)",
                    background: isLight
                      ? "rgba(255, 255, 255, 0.8)"
                      : "rgba(255, 255, 255, 0.06)",
                    boxShadow: isLight
                      ? `0 12px 28px ${alpha(theme.palette.primary.main, 0.12)}`
                      : `0 12px 28px ${alpha(
                          theme.palette.primary.main,
                          0.25,
                        )}`,
                    "& .arrow-icon": {
                      transform: "translateX(4px)",
                      opacity: 1,
                    },
                  },
                }}
              >
                {/* Header Row - Tags and Date */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1.5,
                  }}
                >
                  {/* Left: Category and Status Tags */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    {/* Category */}
                    <Typography
                      variant="caption"
                      sx={{
                        px: 1.2,
                        py: 0.4,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        lineHeight: 1,
                        bgcolor: "transparent",
                        border: `1px solid ${alpha(
                          theme.palette.primary.main,
                          0.25,
                        )}`,
                        borderRadius: 1,
                        display: "inline-block",
                      }}
                    >
                      {topic.category?.category_name || t("common.category")}
                    </Typography>

                    {/* Status */}
                    <Typography
                      variant="caption"
                      sx={{
                        px: 1.2,
                        py: 0.4,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: statusColor,
                        lineHeight: 1,
                        bgcolor: "transparent",
                        border: `1px solid ${alpha(statusColor, 0.25)}`,
                        borderRadius: 1,
                        display: "inline-block",
                      }}
                    >
                      {isContributed
                        ? t("topics.contributed")
                        : t("topics.notContributed")}
                    </Typography>
                  </Box>

                  {/* Right: Date */}
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: "text.secondary",
                      letterSpacing: 0.3,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {topic.createdAt
                      ? moment(topic.createdAt).local().format("MMM DD, YYYY")
                      : "Oct 28, 2024"}
                  </Typography>
                </Box>

                {/* Title */}
                <Typography
                  variant="h6"
                  sx={{
                    mb: 1.2,
                    fontWeight: 600,
                    fontSize: "1.125rem",
                    lineHeight: 1.35,
                    color: "text.primary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    minHeight: 44,
                  }}
                >
                  {topic.topic_name || t("topics.untitledTopic")}
                </Typography>

                {/* Description */}
                <Box
                  sx={{
                    mb: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: isLight
                      ? alpha(theme.palette.grey[500], 0.06)
                      : alpha(theme.palette.grey[700], 0.1),
                    backdropFilter: "blur(10px)",
                    flex: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      lineHeight: 1.55,
                      fontSize: "0.875rem",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {topic.topic_description || "No description available"}
                  </Typography>
                </Box>

                {/* Footer */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mt: "auto",
                  }}
                >
                  {/* Contributors */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        backdropFilter: "blur(10px)",
                        border: "none",
                      }}
                    >
                      <StarsIcon
                        sx={{
                          fontSize: 18,
                          color: theme.palette.success.main,
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          fontSize: "0.7rem",
                          fontWeight: 500,
                          color: "text.secondary",
                          lineHeight: 1,
                          mb: 0.4,
                        }}
                      >
                        Contributors
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          fontSize: "1.05rem",
                          color: "text.primary",
                          lineHeight: 1,
                        }}
                      >
                        {topic.contributor_count || 0}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Arrow */}
                  <Box
                    className="arrow-icon"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.8,
                      px: 1.5,
                      py: 0.8,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      backdropFilter: "blur(10px)",
                      border: "none",
                      transition: "all 0.3s ease",
                      opacity: 0.6,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        lineHeight: 1,
                      }}
                    >
                      Read More
                    </Typography>
                    <ArrowCircleRightOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: theme.palette.primary.main,
                      }}
                    />
                  </Box>
                </Box>
              </CustomBox>
            </Grid>
          );
        })}
      </Grid>

      {/* Pagination Section */}
      {totalItems > 0 && (
        <Box
          sx={{
            mt: 2,
            mb: 2,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            p: 3,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 3,
            border: `1.5px solid ${theme.palette.divider}`,
            boxShadow: isLight
              ? "0 2px 8px 0 rgba(2,89,165,0.07)"
              : "0 4px 18px 0 rgba(0,188,212,0.10)",
          }}
        >
          {/* Items per page selector */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500, minWidth: "max-content" }}
            >
              {t("topics.itemsPerPage")}
            </Typography>
            <FormControl size="small" sx={{ minWidth: 70 }}>
              <Select
                value={filters.limit}
                onChange={handlePageSizeChange}
                sx={{
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.default,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: isLight
                      ? "rgba(2,89,165,0.18)"
                      : "rgba(0,188,212,0.28)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.primary.main,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Page info and pagination */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              gap: 2,
            }}
          >
            {/* Page info */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontWeight: 500,
                minWidth: "max-content",
                order: { xs: 2, sm: 1 },
              }}
            >
              {t("topics.showingTopicsRange", {
                from: showingFrom,
                to: showingTo,
                total: totalItems,
              })}
            </Typography>

            {/* Pagination */}
            <Pagination
              count={Math.ceil(totalItems / filters.limit)}
              page={filters.page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              size="medium"
              sx={{
                order: { xs: 1, sm: 2 },
                "& .MuiPaginationItem-root": {
                  borderRadius: 2,
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor: isLight
                      ? "rgba(2,89,165,0.08)"
                      : "rgba(0,188,212,0.10)",
                  },
                },
                "& .Mui-selected": {
                  backgroundColor: `${theme.palette.primary.main} !important`,
                  color: theme.palette.primary.contrastText,
                  fontWeight: 600,
                  boxShadow: isLight
                    ? "0 2px 8px 0 rgba(2,89,165,0.25)"
                    : "0 2px 8px 0 rgba(0,188,212,0.25)",
                },
              }}
              renderItem={(item) => (
                <PaginationItem
                  slots={{
                    previous: NavigateBeforeIcon,
                    next: NavigateNextIcon,
                  }}
                  {...item}
                />
              )}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default GridView;
